import { useState, useRef } from 'react'
import { useToast } from '../../components/Toast'
import { useAuth } from '../../store/AuthContext'
import { useMutation } from '@tanstack/react-query'
import authService from '../../services/authService'
import api from '../../services/api'

export const ProfilePage = () => {
  const toast = useToast()
  const { user, refetchUser } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isEditingPassword, setIsEditingPassword] = useState(false)
  const [passwords, setPasswords] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  })

  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [profileData, setProfileData] = useState({
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
  })

  // Default avatar
  const defaultAvatar = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect fill='%232563EB' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='40' fill='white'%3E${user?.first_name?.[0] || user?.username?.[0] || '?'}%3C/text%3E%3C/svg%3E`

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: { old_password: string; new_password: string }) =>
      authService.changePassword(data),
    onSuccess: () => {
      toast.success('Senha alterada com sucesso!')
      setIsEditingPassword(false)
      setPasswords({ old_password: '', new_password: '', confirm_password: '' })
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.old_password?.[0] ||
        error.response?.data?.new_password?.[0] ||
        'Erro ao alterar senha.'
      toast.error(message)
    },
  })

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: { email?: string; first_name?: string; last_name?: string }) =>
      authService.updateProfile(data),
    onSuccess: async () => {
      toast.success('Perfil atualizado com sucesso!')
      await refetchUser()
      setIsEditingProfile(false)
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Erro ao atualizar perfil.'
      toast.error(message)
    },
  })

  // Upload avatar mutation
  const uploadAvatarMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('avatar', file)
      const response = await api.post('/auth/upload-avatar/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },
    onSuccess: async () => {
      toast.success('Avatar atualizado com sucesso!')
      await refetchUser()
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        'Erro ao fazer upload do avatar.'
      toast.error(message)
    },
  })

  const handlePasswordChange = () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast.warning('As senhas não coincidem.')
      return
    }

    if (passwords.new_password.length < 8) {
      toast.warning('A nova senha deve ter no mínimo 8 caracteres.')
      return
    }

    changePasswordMutation.mutate({
      old_password: passwords.old_password,
      new_password: passwords.new_password,
    })
  }

  const handleProfileUpdate = () => {
    if (!profileData.email || !profileData.first_name || !profileData.last_name) {
      toast.warning('Todos os campos são obrigatórios.')
      return
    }

    updateProfileMutation.mutate(profileData)
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.warning('Por favor, selecione uma imagem.')
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.warning('Imagem muito grande. Máximo 5MB.')
      return
    }

    uploadAvatarMutation.mutate(file)
  }

  if (!user) return null

  const avatarUrl = (user as any).avatar_url || defaultAvatar

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
        Meu Perfil
      </h1>

      {/* User Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          {/* Avatar with upload */}
          <div className="relative group">
            <img
              src={avatarUrl}
              alt="Avatar"
              className="w-24 h-24 rounded-full object-cover border-4 border-primary/20"
            />
            <button
              onClick={handleAvatarClick}
              disabled={uploadAvatarMutation.isPending}
              className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              <span className="text-white text-sm font-medium">
                {uploadAvatarMutation.isPending ? '⏳' : '📷 Alterar'}
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {user.first_name} {user.last_name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">@{user.username}</p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              Clique no avatar para alterar
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <p className="text-gray-900 dark:text-white">{user.email}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Função
            </label>
            <span
              className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                user.role === 'admin'
                  ? 'bg-danger/10 text-danger'
                  : user.role === 'manager'
                  ? 'bg-warning/10 text-warning'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {user.role === 'admin' ? 'Administrador' : user.role === 'manager' ? 'Gestor' : 'Colaborador'}
            </span>
          </div>
        </div>
      </div>

      {/* Edit Profile Card */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Editar Perfil
          </h3>
          {!isEditingProfile && (
            <button
              onClick={() => {
                setIsEditingProfile(true)
                setProfileData({
                  email: user?.email || '',
                  first_name: user?.first_name || '',
                  last_name: user?.last_name || '',
                })
              }}
              className="px-4 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors"
            >
              Editar Dados
            </button>
          )}
        </div>

        {isEditingProfile && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) =>
                  setProfileData({ ...profileData, email: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="seu.email@exemplo.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nome *
              </label>
              <input
                type="text"
                value={profileData.first_name}
                onChange={(e) =>
                  setProfileData({ ...profileData, first_name: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Seu nome"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sobrenome *
              </label>
              <input
                type="text"
                value={profileData.last_name}
                onChange={(e) =>
                  setProfileData({ ...profileData, last_name: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Seu sobrenome"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleProfileUpdate}
                disabled={
                  updateProfileMutation.isPending ||
                  !profileData.email ||
                  !profileData.first_name ||
                  !profileData.last_name
                }
                className="px-6 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateProfileMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
              </button>
              <button
                onClick={() => {
                  setIsEditingProfile(false)
                  setProfileData({
                    email: user?.email || '',
                    first_name: user?.first_name || '',
                    last_name: user?.last_name || '',
                  })
                }}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-medium rounded-btn transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Change Password Card */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Alterar Senha
          </h3>
          {!isEditingPassword && (
            <button
              onClick={() => setIsEditingPassword(true)}
              className="px-4 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors"
            >
              Trocar Senha
            </button>
          )}
        </div>

        {isEditingPassword && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Senha Atual *
              </label>
              <input
                type="password"
                value={passwords.old_password}
                onChange={(e) =>
                  setPasswords({ ...passwords, old_password: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Digite sua senha atual"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nova Senha * (mínimo 8 caracteres)
              </label>
              <input
                type="password"
                value={passwords.new_password}
                onChange={(e) =>
                  setPasswords({ ...passwords, new_password: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Digite a nova senha"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirmar Nova Senha *
              </label>
              <input
                type="password"
                value={passwords.confirm_password}
                onChange={(e) =>
                  setPasswords({ ...passwords, confirm_password: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Confirme a nova senha"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handlePasswordChange}
                disabled={
                  changePasswordMutation.isPending ||
                  !passwords.old_password ||
                  !passwords.new_password ||
                  !passwords.confirm_password
                }
                className="px-6 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {changePasswordMutation.isPending ? 'Salvando...' : 'Salvar Senha'}
              </button>
              <button
                onClick={() => {
                  setIsEditingPassword(false)
                  setPasswords({ old_password: '', new_password: '', confirm_password: '' })
                }}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-medium rounded-btn transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProfilePage
